<script setup lang="ts">
import { useFullscreen, useSessionStorage } from '@vueuse/core'

const { data: ui } = useGamePresentationUI()
const { enter, isFullscreen } = useFullscreen()

// Persist dismissal across remounts. Without this, an auto-reconnect
// (which unmounts the parent's connected-only overlay slot then remounts
// it once the presenter rejoins) would resurface the fullscreen prompt
// every time the controller flickered, even though the user had already
// answered it once.
const dismissed = useSessionStorage('sfg.presenter.fullscreen-prompt-dismissed', false)
const visible = computed(() => !dismissed.value && !isFullscreen.value)

function dismiss() {
  dismissed.value = true
}

async function goFullscreen() {
  try {
    await enter()
    dismissed.value = true
  } catch {
    // Some browsers (iOS Safari on non-video elements) simply don't support
    // programmatic fullscreen. Dismiss so the presenter view becomes usable.
    dismissed.value = true
  }
}
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    leave-active-class="transition-opacity duration-150"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-center justify-center bg-default/85 p-6 backdrop-blur"
      role="dialog"
      aria-modal="true"
      @click.self="goFullscreen"
    >
      <div class="w-full max-w-md">
        <UCard :ui="{ body: 'bg-muted/5 text-center' }">
          <div class="flex flex-col items-center gap-5 py-4">
            <UIcon
              class="size-12 text-primary"
              name="mdi:fullscreen"
            />
            <h2 class="text-balance text-2xl font-black tracking-tight sm:text-3xl">
              {{ ui?.presenterFullscreenTitle }}
            </h2>
            <p class="max-w-md text-balance text-sm text-muted sm:text-base">
              {{ ui?.presenterFullscreenDescription }}
            </p>
            <div class="w-full flex flex-col gap-2 pt-2">
              <UButton
                block
                icon="mdi:fullscreen"
                :label="ui?.presenterFullscreenEnter"
                size="lg"
                @click="goFullscreen"
              />
              <UButton
                block
                color="neutral"
                variant="ghost"
                :label="ui?.presenterFullscreenSkip"
                size="sm"
                @click="dismiss"
              />
            </div>
          </div>
        </UCard>
      </div>
    </div>
  </Transition>
</template>
